import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import cx from "classnames";
import { getAuthUser } from "../../../hooks/useAuthUser";
import { runGlobalSearch } from "../../../search/globalSearchService";
import {
  MIN_QUERY_LENGTH,
  SEARCH_CATEGORY_LABELS,
  SEARCH_CATEGORY_ORDER,
  type SearchCategory,
  type SearchResult,
} from "../../../search/searchTypes";
import Search from "../../images/sidebar/basil/Search";
import headerStyles from "./Header.module.scss";
import s from "./GlobalSearch.module.scss";

function Highlight({ text, query }: { text: string; query: string }) {
  if (!text) return null;
  const q = query.trim();
  if (!q) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className={s.mark}>{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function groupResults(results: SearchResult[]) {
  return SEARCH_CATEGORY_ORDER.map((category) => ({
    category,
    items: results.filter((item) => item.category === category),
  })).filter((group) => group.items.length > 0);
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const genRef = useRef(0);

  const [query, setQuery] = useState("");
  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const groups = useMemo(() => groupResults(results), [results]);
  const flatResults = useMemo(
    () => groups.flatMap((group) => group.items),
    [groups],
  );

  useEffect(() => {
    const onDocMouseDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setFocus(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    const value = query.trim();
    if (value.length < MIN_QUERY_LENGTH) {
      genRef.current += 1;
      setResults([]);
      setLoading(false);
      return;
    }

    const gen = ++genRef.current;
    const handle = window.setTimeout(() => {
      setLoading(true);
      setResults([]);
      setActiveIndex(0);
      const collected: SearchResult[] = [];
      runGlobalSearch(value, getAuthUser(), {
        isCancelled: () => gen !== genRef.current,
        onPartial: (batch) => {
          if (gen !== genRef.current) return;
          collected.push(...batch);
          setResults([...collected]);
        },
      }).then((finalResults) => {
        if (gen !== genRef.current) return;
        setResults(finalResults);
        setLoading(false);
      });
    }, 300);

    return () => window.clearTimeout(handle);
  }, [query]);

  const goToResult = (item: SearchResult) => {
    setOpen(false);
    setFocus(false);
    inputRef.current?.blur();
    navigate(item.path, { state: item.state });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open || !flatResults.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatResults.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = flatResults[activeIndex];
      if (item) goToResult(item);
    }
  };

  const showPanel = open && query.trim().length >= MIN_QUERY_LENGTH;
  const trimmed = query.trim();

  return (
    <div className={s.wrap} ref={wrapRef}>
      <div
        className={cx("input-group-no-border", { focus: !!focus })}
        onFocus={() => {
          setFocus(true);
          setOpen(true);
        }}
      >
        <div className={cx(headerStyles.headerSvgFlipColor, "input-group-prepend-icon")}>
          <Search />
        </div>
        <input
          ref={inputRef}
          id="search-input"
          className={cx("form-control", { focus: !!focus })}
          placeholder="Search Dashboard"
          aria-label="Search Dashboard"
          value={query}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={showPanel}
          aria-controls="global-search-results"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
      </div>

      {showPanel && (
        <div
          id="global-search-results"
          className={s.panel}
          role="listbox"
        >
          {loading && results.length === 0 && (
            <div className={s.loading}>
              <span className={s.spinner} />
              Searching…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className={s.empty}>No matching pages or records.</div>
          )}
          {groups.map((group) => {
            const label = SEARCH_CATEGORY_LABELS[group.category as SearchCategory];
            return (
              <div key={group.category}>
                <div className={s.groupHeader}>{label}</div>
                {group.items.map((item) => {
                  const index = flatResults.indexOf(item);
                  return (
                    <button
                      type="button"
                      key={item.id}
                      role="option"
                      aria-selected={index === activeIndex}
                      className={cx(s.item, index === activeIndex && s.itemActive)}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => goToResult(item)}
                    >
                      <span>
                        <div className={s.itemTitle}>
                          <Highlight text={item.title} query={trimmed} />
                        </div>
                        {item.subtitle ? (
                          <div className={s.itemSubtitle}>
                            <Highlight text={item.subtitle} query={trimmed} />
                          </div>
                        ) : null}
                      </span>
                      {item.badge ? <span className={s.badge}>{item.badge}</span> : null}
                    </button>
                  );
                })}
              </div>
            );
          })}
          {loading && results.length > 0 && (
            <div className={s.loading}>
              <span className={s.spinner} />
              Loading more…
            </div>
          )}
        </div>
      )}
    </div>
  );
}
